// ─── DOM shell ──────────────────────────────────────────────────────────────
// HUD + overlays around the game canvas. The canvas is the only rendering
// surface for the game itself; everything here is chrome.

import type { LeaderboardEntry } from '@gametypes';
import { SITE_URL } from './config';
import { BUNDLE_PRICE_LABEL, type WebSkin } from './skins';

export interface UICallbacks {
  onPlayDaily: () => void;
  onPlayAgain: () => void;
  onPause: () => void;
  onResume: () => void;
  onQuit: () => void;
  onShare: () => void;
  onOpenLeaderboard: () => void;
  onOpenSkins: () => void;
}

export interface SkinsCallbacks {
  onSelect: (id: string) => void;
  onBuy: (entitlementId: string) => void;
  onBuyBundle: () => void;
}

const h = (tag: string, cls?: string, text?: string): HTMLElement => {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (text != null) el.textContent = text;
  return el;
};

export class UI {
  root: HTMLElement;
  canvas: HTMLCanvasElement;
  private cb: UICallbacks;

  private scoreEl = h('div', 'hud-score', '0');
  private comboEl = h('div', 'hud-combo', '');
  private streakEl = h('div', 'hud-streak', '');
  private pbEl = h('div', 'hud-pb', '');
  private pauseBtn = h('button', 'icon-btn');

  private landing = h('div', 'overlay');
  private pauseScreen = h('div', 'overlay');
  private gameOver = h('div', 'overlay');
  private leaderboard = h('div', 'modal-backdrop');
  private lbTimer: number | null = null;
  private boardWrap!: HTMLElement;
  private themeLabel = h('div', 'landing-theme', '');

  private skinsModal = h('div', 'modal-backdrop');
  private skinsNote: HTMLElement | null = null;

  private goStats = h('div', 'go-stats');
  private shareBtn = h('button', 'btn btn-primary', 'Share your result');
  private shareNote = h('div', 'share-note', '');
  private goTitle = h('h2', 'overlay-title', 'Board complete');
  private goNote = h('p', 'go-note', '');
  private goLeaderboardBtn = h('button', 'btn btn-ghost', 'View leaderboard');
  private goPlayBtn = h('button', 'btn btn-primary', 'Play again');

  constructor(mount: HTMLElement, cb: UICallbacks) {
    this.cb = cb;
    this.root = h('div', 'game-root');

    // HUD
    const hud = h('div', 'hud');
    const left = h('div', 'hud-left');
    left.append(this.scoreEl, this.pbEl);
    const right = h('div', 'hud-right');
    this.pauseBtn.setAttribute('aria-label', 'Pause');
    this.pauseBtn.innerHTML = '<span></span><span></span>';
    this.pauseBtn.addEventListener('click', () => this.cb.onPause());
    this.pauseBtn.hidden = true;
    right.append(this.streakEl, this.pauseBtn);
    hud.append(left, this.comboEl, right);

    // canvas — wrapped in a flex-sized box; the canvas itself is
    // position:absolute inset:0 so its layout size is the wrapper's, fully
    // decoupled from its width/height attributes (which the renderer sets to
    // cssSize*dpr — without this the element grows to that backing size and
    // feeds back, blowing the board off-screen on high-DPR phones).
    this.canvas = h('canvas', 'board') as HTMLCanvasElement;
    const boardWrap = h('div', 'board-wrap');
    boardWrap.append(this.canvas);
    this.boardWrap = boardWrap;

    // landing
    const privacyLink = h('a', 'landing-legal', 'Privacy') as HTMLAnchorElement;
    privacyLink.href = '/privacy';
    this.landing.append(
      h('h1', 'landing-title', 'SortBeat'),
      h('p', 'landing-tag', 'Route the colours. Ride the beat. Watch it cascade.'),
      this.themeLabel,
      this.primaryButton("Play today's board", () => this.cb.onPlayDaily()),
      this.ghostButton('Skins', () => this.cb.onOpenSkins()),
      h('p', 'landing-hint', 'Tap a lane to route the token. Tap it again to drop on the beat.'),
      privacyLink,
    );

    // pause
    this.pauseScreen.append(
      h('h2', 'overlay-title', 'Paused'),
      this.primaryButton('Resume', () => this.cb.onResume()),
      this.ghostButton('Quit to menu', () => this.cb.onQuit()),
    );
    this.pauseScreen.hidden = true;

    // game over
    const goInner = h('div', 'go-inner');
    this.shareBtn.addEventListener('click', () => this.cb.onShare());
    this.goLeaderboardBtn.addEventListener('click', () => this.cb.onOpenLeaderboard());
    this.goPlayBtn.addEventListener('click', () => this.cb.onPlayAgain());
    goInner.append(
      this.goTitle,
      this.goNote,
      this.goStats,
      this.shareBtn,
      this.shareNote,
      this.goLeaderboardBtn,
      this.goPlayBtn,
      this.ghostButton('Menu', () => this.cb.onQuit()),
    );
    this.gameOver.append(goInner);
    this.gameOver.hidden = true;

    // leaderboard
    this.leaderboard.hidden = true;
    this.leaderboard.addEventListener('click', (e) => {
      if (e.target === this.leaderboard) this.hideLeaderboard();
    });

    // skins
    this.skinsModal.hidden = true;
    this.skinsModal.addEventListener('click', (e) => {
      if (e.target === this.skinsModal) this.hideSkins();
    });

    this.root.append(
      hud,
      this.boardWrap,
      this.landing,
      this.pauseScreen,
      this.gameOver,
      this.leaderboard,
      this.skinsModal,
    );
    mount.append(this.root);
  }

  private primaryButton(label: string, on: () => void): HTMLElement {
    const b = h('button', 'btn btn-primary', label);
    b.addEventListener('click', on);
    return b;
  }
  private ghostButton(label: string, on: () => void): HTMLElement {
    const b = h('button', 'btn btn-ghost', label);
    b.addEventListener('click', on);
    return b;
  }

  // ── state ────────────────────────────────────────────────────────────────
  setTheme(name: string): void {
    this.themeLabel.textContent = `Today: ${name}`;
  }

  setScore(score: number, combo: number): void {
    this.scoreEl.textContent = score.toLocaleString();
    this.comboEl.textContent = combo > 1 ? `×${combo}` : '';
  }

  setPersonalBest(delta: number): void {
    this.pbEl.textContent = delta > 0 ? `+${delta.toLocaleString()} PB` : '';
  }

  setStreak(days: number): void {
    this.streakEl.textContent = days > 0 ? `🔥 ${days}` : '';
  }

  showPlaying(): void {
    this.landing.hidden = true;
    this.pauseScreen.hidden = true;
    this.gameOver.hidden = true;
    this.pauseBtn.hidden = false;
  }

  showLanding(): void {
    this.landing.hidden = false;
    this.pauseScreen.hidden = true;
    this.gameOver.hidden = true;
    this.pauseBtn.hidden = true;
    this.setScore(0, 1);
    this.setPersonalBest(0);
  }

  showPaused(): void {
    this.pauseScreen.hidden = false;
  }
  hidePaused(): void {
    this.pauseScreen.hidden = true;
  }

  showGameOver(stats: {
    score: number;
    chains: number;
    beatPct: number;
    maxCombo: number;
    isPb: boolean;
    onboarding?: boolean;
  }): void {
    this.pauseBtn.hidden = true;
    this.goStats.innerHTML = '';
    const rows: [string, string][] = [
      ['Score', stats.score.toLocaleString() + (stats.isPb ? '  🏆 best' : '')],
      ['Chains', String(stats.chains)],
      ['On beat', stats.chains > 0 ? stats.beatPct + '%' : '—'],
      ['Best combo', '×' + stats.maxCombo],
    ];
    for (const [k, v] of rows) {
      const row = h('div', 'go-row');
      row.append(h('span', 'go-k', k), h('span', 'go-v', v));
      this.goStats.append(row);
    }
    this.shareNote.textContent = '';

    // Onboarding was a fixed practice board (ONBOARDING_SEED, not the daily
    // seed) — its score can't go on the shared leaderboard. Make that clear
    // instead of showing "play today's board to get on it" with no context.
    const ob = stats.onboarding === true;
    this.goTitle.textContent = ob ? 'Warm-up done' : 'Board complete';
    this.goNote.textContent = ob
      ? "That was a practice board. Play today's real board — everyone plays the same one — to get on the leaderboard."
      : '';
    this.goNote.hidden = !ob;
    this.shareBtn.hidden = ob;
    this.goLeaderboardBtn.hidden = ob;
    this.goPlayBtn.textContent = ob ? "Play today's board" : 'Play again';

    this.gameOver.hidden = false;
  }

  setShareNote(text: string): void {
    this.shareNote.textContent = text;
  }

  // ── leaderboard ──────────────────────────────────────────────────────────
  showLeaderboard(
    entries: LeaderboardEntry[],
    myScore: number | null,
    opts: { themeName: string; date: string },
  ): void {
    const panel = h('div', 'modal');
    panel.append(h('h2', 'modal-title', opts.themeName || "Today's board"));

    // The board is one global puzzle keyed to the UTC date. Show that date as
    // its label (unambiguous — it's the key, not "your" calendar day) and a
    // live countdown to the next one, so "it's a daily" reads without a
    // timezone-mismatched weekday.
    const sub = h('div', 'lb-sub');
    sub.append(h('span', 'lb-date', `Daily board · ${opts.date}`));
    const countdown = h('span', 'lb-countdown', '');
    sub.append(countdown);
    panel.append(sub);
    const tickCountdown = (): void => {
      const now = Date.now();
      const next = Date.UTC(
        new Date().getUTCFullYear(),
        new Date().getUTCMonth(),
        new Date().getUTCDate() + 1,
      );
      const s = Math.max(0, Math.floor((next - now) / 1000));
      const hh = Math.floor(s / 3600);
      const mm = Math.floor((s % 3600) / 60);
      countdown.textContent = `New board in ${hh > 0 ? `${hh}h ` : ''}${mm}m`;
    };
    tickCountdown();
    this.clearLbTimer();
    this.lbTimer = window.setInterval(tickCountdown, 30_000);

    if (entries.length === 0) {
      panel.append(h('p', 'modal-empty', 'Be the first score on the board today.'));
    } else {
      const list = h('div', 'lb-list');
      for (const e of entries.slice(0, 15)) {
        const row = h('div', 'lb-row');
        if (myScore != null && e.score === myScore) row.classList.add('lb-row-me');
        const t = new Date(e.submittedAt);
        row.append(
          h('span', 'lb-rank', '#' + e.rank),
          h('span', 'lb-score', e.score.toLocaleString()),
          h('span', 'lb-chains', e.chainsCleared + ' chains'),
          h(
            'span',
            'lb-time',
            isNaN(t.getTime())
              ? ''
              : t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
          ),
        );
        list.append(row);
      }
      panel.append(list);
      panel.append(h('p', 'lb-me', this.myStandingLine(entries, myScore)));
    }

    const close = h('button', 'btn btn-ghost', 'Close');
    close.addEventListener('click', () => this.hideLeaderboard());
    panel.append(close);
    this.leaderboard.innerHTML = '';
    this.leaderboard.append(panel);
    this.leaderboard.hidden = false;
  }

  /** "gap to the top 10" framing — never a demoralising raw rank at soft-launch scale. */
  private myStandingLine(entries: LeaderboardEntry[], myScore: number | null): string {
    if (myScore == null) return 'Play today’s board to get on it.';
    const me = `Your score: ${myScore.toLocaleString()}`;
    if (entries.length >= 10) {
      const top10 = entries[9].score;
      if (myScore >= entries[0].score) return `${me} — top of the board 🏆`;
      if (myScore >= top10) return `${me} — you’re in the top 10`;
      return `${me} — ${(top10 - myScore).toLocaleString()} to reach the top 10`;
    }
    return me;
  }

  private clearLbTimer(): void {
    if (this.lbTimer != null) {
      window.clearInterval(this.lbTimer);
      this.lbTimer = null;
    }
  }

  hideLeaderboard(): void {
    this.clearLbTimer();
    this.leaderboard.hidden = true;
  }

  // ── skins ────────────────────────────────────────────────────────────────
  showSkins(
    skins: WebSkin[],
    unlockedIds: string[],
    currentId: string,
    bundleOwned: boolean,
    cb: SkinsCallbacks,
  ): void {
    const panel = h('div', 'modal');
    panel.append(h('h2', 'modal-title', 'Skins'));

    const list = h('div', 'skins-list');
    for (const s of skins) {
      const row = h('div', 'skin-row');
      if (s.id === currentId) row.classList.add('skin-row-active');
      const swatch = h('span', 'skin-swatch');
      swatch.style.background = s.accentColor;
      row.append(swatch, h('span', 'skin-name', s.name));

      const owned = s.free || unlockedIds.includes(s.id);
      if (owned) {
        const label = s.id === currentId ? 'Selected' : 'Select';
        const btn = h('button', 'btn btn-ghost skin-btn', label) as HTMLButtonElement;
        if (s.id === currentId) {
          btn.disabled = true;
        } else {
          btn.addEventListener('click', () => cb.onSelect(s.id));
        }
        row.append(btn);
      } else {
        const btn = h('button', 'btn btn-primary skin-btn', `Buy ${s.priceLabel}`);
        btn.addEventListener('click', () => cb.onBuy(s.entitlementId!));
        row.append(btn);
      }
      list.append(row);
    }
    panel.append(list);

    if (!bundleOwned) {
      const bundleRow = h('div', 'skin-bundle-row');
      bundleRow.append(h('span', 'skin-bundle-label', 'Get every skin, one price'));
      const bundleBtn = h('button', 'btn btn-primary', `Buy all — ${BUNDLE_PRICE_LABEL}`);
      bundleBtn.addEventListener('click', () => cb.onBuyBundle());
      bundleRow.append(bundleBtn);
      panel.append(bundleRow);
    }

    this.skinsNote = h('p', 'skins-note', '');
    panel.append(this.skinsNote);

    const close = h('button', 'btn btn-ghost', 'Close');
    close.addEventListener('click', () => this.hideSkins());
    panel.append(close);

    this.skinsModal.innerHTML = '';
    this.skinsModal.append(panel);
    this.skinsModal.hidden = false;
  }

  setSkinsNote(text: string): void {
    if (this.skinsNote) this.skinsNote.textContent = text;
  }

  hideSkins(): void {
    this.skinsModal.hidden = true;
  }
}

export { SITE_URL };
